import { capitalize } from "lodash";
import { useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import {
  useAccount, useContact, useWorkspaceObjectsDetailed,
} from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import UserProfileHeader from "./components/UserProfileHeader";
import UserRoutes from "./components/UserRoutes";

const UserDetailsPage = () => {
  const { accountId, userId, workspaceId: routeWorkspaceId, viewId }: any = useParams();
  const navigate = useNavigate();
  const currentWorkspace = useWorkspaceStore((state) => state.workspace);
  const workspaceId = currentWorkspace?.id;

  const { data: contact }: any = useContact({
    workspaceId,
    contactId: userId,
    enabled: !!userId && !!workspaceId,
  });

  const { data: account }: any = useAccount({
    workspaceId,
    accountId,
    enabled: !!accountId && !!workspaceId,
  });

  // Prefetch custom objects so data is cached when UserRoutes renders
  useWorkspaceObjectsDetailed({
    workspaceId: workspaceId || "",
    enabled: !!workspaceId,
  });

  useEffect(() => {
    document.title = `${contact?.name ? `${capitalize(contact?.name)} | ` : ""}${account?.name ? `${account.name} | ` : ""}User | Hyperengage`;
  }, [contact?.name, account?.name]);

  const handleBack = () => {
    if (viewId) {
      navigate(`/home/${routeWorkspaceId}/view/${viewId}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-90px)] overflow-hidden bg-[#FCFCFC]">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 px-6 py-1.5 border-b border-gray-200 bg-white min-h-0">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-0.5 px-1.5 py-0.5 text-[12px] text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {account?.name || "Back"}
        </button>
        <span className="text-gray-300 text-[12px]">/</span>
        <span className="text-[12px] font-medium text-gray-900">
          {contact?.name || contact?.email || "Loading..."}
        </span>
      </div>

      {/* Main content: sidebar + tabs */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-[280px] min-w-[280px] border-r border-gray-200 bg-white overflow-y-auto">
          <UserProfileHeader user={contact} />
        </div>

        {/* Main content: tabs + outlet */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="border-b border-gray-200 bg-white px-6 pt-0.5">
            <UserRoutes />
          </div>
          <div className="flex-1 overflow-y-auto bg-white">
            <div className="p-6">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsPage;
